import { Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import type { CategoryDailyDidLiftRow, RevenueSource } from '../../types/experiment.types';
import { formatPercentFromPercent } from '../../utils/format-percent';

import { RevenueSourceTag } from './revenue-source-tag';

interface Props {
  rows: CategoryDailyDidLiftRow[];
  isLoading?: boolean | undefined;
  /** When true the body is replaced with an error card; header stays visible. */
  isError?: boolean | undefined;
  /** Source of the revenue figures; null/absent renders no tag. */
  revenueSource?: RevenueSource | null | undefined;
}

const COLUMNS: { key: keyof CategoryDailyDidLiftRow; labelKey: string }[] = [
  { key: 'category', labelKey: 'detail.categoryDailyDidLift.columns.category' },
  { key: 'weekLabel', labelKey: 'detail.categoryDailyDidLift.columns.week' },
  { key: 'weekStart', labelKey: 'detail.categoryDailyDidLift.columns.weekStart' },
  { key: 'testPreAvg', labelKey: 'detail.categoryDailyDidLift.columns.testPreAvg' },
  { key: 'testPostRev', labelKey: 'detail.categoryDailyDidLift.columns.testPostRev' },
  { key: 'controlPreAvg', labelKey: 'detail.categoryDailyDidLift.columns.controlPreAvg' },
  { key: 'controlPostRev', labelKey: 'detail.categoryDailyDidLift.columns.controlPostRev' },
  { key: 'didRevenuePct', labelKey: 'detail.categoryDailyDidLift.columns.didRevenuePct' },
];

const COLUMN_DEFAULTS: Record<keyof CategoryDailyDidLiftRow, string> = {
  category: 'Category',
  weekLabel: 'Week',
  weekStart: 'Week Start',
  testPreAvg: 'Test Pre-Avg ($)',
  testPostRev: 'Test Post Rev. ($)',
  controlPreAvg: 'Control Pre-Avg ($)',
  controlPostRev: 'Control Post Rev. ($)',
  didRevenuePct: 'DID % Revenue',
};

/**
 * Per-week DID-lift breakdown for the selected category — paired with the
 * Weekly DiD Revenue Section above. Currency cells are formatted to one
 * decimal; DID% cells are sign-tinted.
 */
export function ExperimentCategoryDailyDidLiftTable({
  rows,
  isLoading,
  isError,
  revenueSource,
}: Props) {
  const { t } = useTranslation('experiments');
  const isEmpty = !isLoading && !isError && rows.length === 0;

  // Exports rows with the same formatting shown in the table cells.
  const handleExport = () => {
    const csvRows = rows.map((row) => ({
      category: row.category,
      weekLabel: row.weekLabel,
      weekStart: row.weekStart,
      testPreAvg: formatDollar(row.testPreAvg),
      testPostRev: formatDollar(row.testPostRev),
      controlPreAvg: formatDollar(row.controlPreAvg),
      controlPostRev: formatDollar(row.controlPostRev),
      didRevenuePct: formatPercentFromPercent(row.didRevenuePct, { precision: 'chart-1dp' }),
    }));
    downloadCsv(
      'daily-did-lift-analysis',
      csvRows,
      COLUMNS.map((col) => ({ key: col.key, label: t(col.labelKey, COLUMN_DEFAULTS[col.key]) })),
    );
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-4 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-3xl space-y-1">
            <h3 className="flex flex-wrap items-center gap-2 text-[16px] font-medium">
              {t('detail.categoryDailyDidLift.title', 'Daily DID Lift Analysis')}
              <RevenueSourceTag source={revenueSource} />
            </h3>
            <p className="text-muted-foreground text-sm">
              {t(
                'detail.categoryDailyDidLift.subtitle',
                'Non-cumulative daily lift values · Post ramp-up (Day 3-18)',
              )}
            </p>
            <p className="text-muted-foreground text-xs">
              {t(
                'detail.categoryDailyDidLift.subtitleSecondary',
                'All metrics shown · DID lift values (Test vs. Control difference)',
              )}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isEmpty}
            className={cn('gap-2', isEmpty ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('detail.categoryDailyDidLift.exportCsv', 'Export CSV')}
          </Button>
        </div>

        {/* Error / empty / table — body slot */}
        {isError ? (
          <div className="border-error/30 bg-error-50 space-y-1 rounded-md border p-4">
            <p className="text-foreground text-sm font-medium">
              {t('detail.categoryDailyDidLift.errorTitle', 'Couldn’t load daily lift data.')}
            </p>
            <p className="text-muted-foreground text-xs leading-5">
              {t('detail.categoryDailyDidLift.errorBody', 'Refresh the page to try again.')}
            </p>
          </div>
        ) : !isLoading && rows.length === 0 ? (
          <div className="bg-muted/30 rounded-md border border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              {t(
                'detail.categoryDailyDidLift.empty',
                'No daily lift rows for the selected category yet.',
              )}
            </p>
          </div>
        ) : (
          <div className="max-h-100 overflow-auto rounded-md border">
            <table className="w-full min-w-[840px] text-sm">
              <thead className="bg-background sticky top-0 z-10">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="text-muted-foreground px-4 py-3 text-left font-medium"
                    >
                      {t(col.labelKey, COLUMN_DEFAULTS[col.key])}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i} className="border-t">
                        {COLUMNS.map((col) => (
                          <td key={col.key} className="px-4 py-3">
                            <Skeleton className="h-4 w-16" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : rows.map((row) => (
                      <tr key={`${row.category}-${row.weekLabel}`} className="border-t">
                        <td className="text-foreground px-4 py-3 font-medium">{row.category}</td>
                        <td className="text-muted-foreground px-4 py-3">{row.weekLabel}</td>
                        <td className="text-muted-foreground px-4 py-3 tabular-nums">
                          {row.weekStart}
                        </td>
                        <td className="text-muted-foreground px-4 py-3 tabular-nums">
                          {formatDollar(row.testPreAvg)}
                        </td>
                        <td className="text-muted-foreground px-4 py-3 tabular-nums">
                          {formatDollar(row.testPostRev)}
                        </td>
                        <td className="text-muted-foreground px-4 py-3 tabular-nums">
                          {formatDollar(row.controlPreAvg)}
                        </td>
                        <td className="text-muted-foreground px-4 py-3 tabular-nums">
                          {formatDollar(row.controlPostRev)}
                        </td>
                        <td
                          className={cn('px-4 py-3 tabular-nums', signTextClass(row.didRevenuePct))}
                        >
                          {formatPercentFromPercent(row.didRevenuePct, { precision: 'chart-1dp' })}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatDollar(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function signTextClass(value: number): string {
  if (value > 0) return 'text-success font-medium';
  if (value < 0) return 'text-error font-medium';
  return 'text-muted-foreground';
}
